import {action, computed, observable, toJS, utils} from '@api'
import {FIT_TO_SCREEN_PADDING, WhiteboardStore} from '@modules/whiteboard'
import {TLBounds, Utils} from '@tldraw/core'
import Vec from '@tldraw/vec'

export class WhiteboardCameraStore {
  constructor(public store: WhiteboardStore) {
  }

  /**
   * When renderer bounds are first set, initialize our viewport based on our saved zoom level
   * Once we're initialized, changes to renderer bounds should have us zoom in/out to maintain the same viewport
   */
  @observable initialized: boolean = false

  get pageState() { return this.store.myPageState}
  get appState() { return this.store.appState}

  /**
   * Update the bounding box when the renderer's bounds change.
   * @param bounds
   */
  @action updateBounds = (bounds: TLBounds) => {
    this.appState.rendererBounds = bounds
    const {point, zoom} = this.pageState.camera

    //this.updateViewport(point, zoom)

    const {store: {readOnly, tools: {session}}} = this
    if (!readOnly && session) {
      session.update()
    }
  }

  get settings() { return this.store.settings.ui}
  get rendererBounds() { return this.appState.rendererBounds}

  // calculateZoomForViewport = (desiredViewport: TLBounds) => {
  //   const {width, height} = this.rendererBounds,
  //     {} = desiredViewport
  // }

  @action updateViewport = (point: number[], zoom: number) => {
    const {width, height} = this.rendererBounds
    const [minX, minY] = Vec.sub(Vec.div([0, 0], zoom), point)
    const [maxX, maxY] = Vec.sub(Vec.div([width, height], zoom), point)

    this.settings.viewport = {
      minX,
      minY,
      maxX,
      maxY,
      height: maxY - minY,
      width: maxX - minX
    }

    // console.log(`Updated viewport`, toJS({point, zoom, viewport: this.settings.viewport, width, height}, {recurseEverything: true}))
  }


  /* -------------------------------------------------- */
  /*                       Camera                       */
  /* -------------------------------------------------- */

  /**
   * Set the camera to a specific point and zoom.
   * @param point The camera point (top left of the viewport).
   * @param zoom The zoom level.
   * @param reason Why did the camera change?
   */
  @action setCamera = (point: number[], zoom: number = this.store.camera.zoom, reason?: string): this => {
    this.updateViewport(point, zoom)
    // What I want to do:
    this.pageState.camera = {point, zoom}
    // Doing this kind of thing sucks!
    // this.store.selectedPageInfo.state = {...this.pageState, camera: {point, zoom}}

    // console.log(`setCamera`, {point, zoom, reason})
    return this
  }

  @computed get rendererCenterPoint() {
    const {width, height} = this.rendererBounds
    return Vec.toFixed([width / 2, height / 2])
  }

  @observable previousViewport : TLBounds
  @computed get viewport() {
    return this.store.ui.viewport
  }
  @computed get viewportCenterPoint() {
    const {store: {getPagePoint, ui: {viewport}}} = this
    return getPagePoint([(viewport.maxX - viewport.minY) / 2, (viewport.maxY - viewport.minY) / 2])
  }

  /**
   * Reset the camera to the default position
   */
  @action resetCamera = (): this => {
    return this.setCamera(this.rendererCenterPoint, 1, `reset_camera`)
  }

  /**
   * Pan the camera
   * @param delta
   */
  @action pan = (delta: number[]): this => {
    const {camera} = this.pageState
    return this.setCamera(Vec.toFixed(Vec.sub(camera.point, delta)), camera.zoom, `panned`)
  }

  /**
   * Pinch to a new zoom level, possibly together with a pan.
   * @param point The current point under the cursor.
   * @param delta The movement delta.
   * @param zoomDelta The zoom detal
   */
  @action pinchZoom = (point: number[], delta: number[], zoom: number): this => {
    const {camera} = this.pageState
    const nextPoint = Vec.sub(camera.point, Vec.div(delta, camera.zoom))
    const nextZoom = zoom
    const p0 = Vec.sub(Vec.div(point, camera.zoom), nextPoint)
    const p1 = Vec.sub(Vec.div(point, nextZoom), nextPoint)
    return this.setCamera(Vec.toFixed(Vec.add(nextPoint, Vec.sub(p1, p0))), nextZoom, `pinch_zoomed`)
  }

  /**
   * Zoom to a new zoom level, keeping the point under the cursor in the same position
   * @param next The new zoom level.
   * @param center The point to zoom towards (defaults to screen center).
   */
  @action zoomTo = (next: number, center = this.rendererCenterPoint): this => {
    const {zoom, point} = this.pageState.camera
    const p0 = Vec.sub(Vec.div(center, zoom), point)
    const p1 = Vec.sub(Vec.div(center, next), point)
    return this.setCamera(Vec.toFixed(Vec.add(point, Vec.sub(p1, p0))), next, `zoomed_camera`)
  }

  minZoom = .1
  maxZoom = 5

  @observable animating = false
  animatedZoomTo = async (next, duration = 150) => {
    this.animating = true

    let {zoom} = this,
      delta = next - zoom,
      fps = 90,
      sleepDuration = 1000 / fps, // / 2 because there's a css transform of the same length
      frames = duration / sleepDuration,
      loopDelta = delta / frames

    // console.log({frames, fps, loopDelta, sleepDuration})
    for (let i = 0; i < Math.max(1, frames); i++) {
      zoom += loopDelta
      this.zoomTo(zoom)
      await utils.sleep(sleepDuration * 2) // * 2 so that the css transition can run
    }

    this.zoomTo(next)

    this.animating = false

    return this
  }

  getCameraZoom(zoom: number) {
    return Utils.clamp(zoom, this.minZoom, this.maxZoom)
  }

  /**
   * Zoom out by 25%
   */
  @action zoomIn = () => {
    const i = Math.round((this.pageState.camera.zoom * 100) / 25)
    const nextZoom = Math.min(this.maxZoom, this.getCameraZoom((i + 1) * 0.25))
    this.animatedZoomTo(nextZoom)
  }

  /**
   * Zoom in by 25%.
   */
  @action zoomOut = () => {
    const i = Math.round((this.pageState.camera.zoom * 100) / 25)
    const nextZoom = Math.max(this.minZoom, this.getCameraZoom((i - 1) * 0.25))
    this.animatedZoomTo(nextZoom)
  }

  /**
   * Zoom to fit the page's shapes.
   */
  @action zoomToFit = (): this => {
    const {store: {shapes, shapeUtilFor}} = this

    if (shapes.length === 0) return this

    const {rendererBounds} = this

    const commonBounds = Utils.getCommonBounds(shapes.map(s => shapeUtilFor(s).getBounds(s)))

    let zoom = this.getCameraZoom(
      Math.min(
        (rendererBounds.width - FIT_TO_SCREEN_PADDING) / commonBounds.width,
        (rendererBounds.height - FIT_TO_SCREEN_PADDING) / commonBounds.height
      )
    )

    zoom = this.pageState.camera.zoom === zoom || this.pageState.camera.zoom < 1
           ? Math.min(1, zoom)
           : zoom

    const mx = (rendererBounds.width - commonBounds.width * zoom) / 2 / zoom
    const my = (rendererBounds.height - commonBounds.height * zoom) / 2 / zoom

    return this.setCamera(
      Vec.toFixed(Vec.sub([mx, my], [commonBounds.minX, commonBounds.minY])),
      zoom,
      `zoomed_to_fit`
    )
  }

  private _getCameraZoom(zoom: number) {
    return Utils.clamp(zoom, 0.1, 5)
  }

  /**
   * Zoom to the selected shapes.
   */
  @action zoomToSelection = (zoomToUse?: number) => {
    if (this.store.selection.selectedIds.length === 0) return this

    const {rendererBounds, store: {selection: {selectedBounds}}} = this

    let zoom = zoomToUse ?? this._getCameraZoom(
      Math.min(
        (rendererBounds.width - FIT_TO_SCREEN_PADDING) / selectedBounds.width,
        (rendererBounds.height - FIT_TO_SCREEN_PADDING) / selectedBounds.height
      )
    )

    zoom = zoomToUse ?? (this.pageState.camera.zoom === zoom || this.pageState.camera.zoom < 1
      ? Math.min(1, zoom)
      : zoom)

    const mx = (rendererBounds.width - selectedBounds.width * zoom) / 2 / zoom
    const my = (rendererBounds.height - selectedBounds.height * zoom) / 2 / zoom

    return this.setCamera(
      Vec.toFixed(Vec.sub([mx, my], [selectedBounds.minX, selectedBounds.minY])),
      zoom,
      `zoomed_to_selection`
    )
  }

  @computed get shapeBounds() {
    const {store: {shapes, shapeUtilFor}} = this,
      shapeBounds = shapes.map(s => shapeUtilFor(s).getBounds(s))

    return shapeBounds
  }

  @computed get commonBounds() {
    const {shapeBounds: bounds} = this

    if (bounds.length < 2) return bounds[0]

    let result = bounds[0]

    for (let i = 1; i < bounds.length; i++) {
      result = Utils.getExpandedBounds(result, bounds[i])
    }

    return result
  }

  /**
   * Zoom back to content when the canvas is empty.
   */
  @action zoomToContent = () => {
    const {store: {shapes}} = this

    if (shapes.length === 0) return

    const {setCamera, rendererBounds, commonBounds, pageState: {camera: {zoom}}} = this,
      mx = (rendererBounds.width - commonBounds.width * zoom) / 2 / zoom,
      my = (rendererBounds.height - commonBounds.height * zoom) / 2 / zoom

    return setCamera(
      Vec.toFixed(Vec.sub([mx, my], [commonBounds.minX, commonBounds.minY])),
      zoom,
      `zoomed_to_content`
    )
  }

  /**
   * Zoom the camera to 100%.
   */
  @action resetZoom = () => {
    this.zoomToContent()
    this.animatedZoomTo(1)
  }

  get zoom() {
    return this.pageState.camera.zoom
  }

  set zoom(v) { this.pageState.camera.zoom = v}

  /**
   * Zoom the camera by a certain delta.
   * @param delta The zoom delta.
   * @param center The point to zoom toward.
   */
  @action zoomBy = Utils.throttle((delta: number, center?: number[]): this => {
    const {zoom} = this.pageState.camera
    const nextZoom = this.getCameraZoom(zoom - delta * zoom)
    return this.zoomTo(nextZoom, center)
  }, 16)
}



// WEBPACK FOOTER //
// ./src/modules/whiteboard/WhiteboardCameraStore.ts